import {computed, ElementRef, Injectable, ViewChild} from '@angular/core';
import {
  AdminService,
  BasicTaDetails,
  FullTaDetails,
  ProjectDetailsListRestResponse,
  ReportsService,
  SubmissionSearchCriteria,
  SubmissionSearchResult,
  SubmissionSearchResultRestResponse,
  SubmissionService,
  UserDetailsListRestResponse,
  UserService
} from '../api';
import {TaAppStore} from '../state/clcta-store';
import {LoadingService} from '../services/loading.service';
import {AdvancedSearchParameters, DelegateTaMode, SearchTaList, UnitTaMode} from '../util/app.types';
import {isListCacheFresh} from '../util/ta-list-cache.utils';
import {HttpResponse} from '@angular/common/http';
import {map, Observable, of} from 'rxjs';
import {ErrorMessageService} from '../services/error-message.service';


/** Advanced search parameters as the server's criteria - shared by search, load-more and export. */
export function toSubmissionSearchCriteria(
  searchParameters: AdvancedSearchParameters,
  page?: number,
): SubmissionSearchCriteria {
  return {
    travellerIds: searchParameters.travellerList?.map(traveller => traveller.id ?? '') ?? [],
    approverIds: searchParameters.approverList?.map(approver => approver.id ?? '') ?? [],
    createdByIds: searchParameters.createdByList?.map(creator => creator.id ?? '') ?? [],
    projectIds: searchParameters.projectList?.map(project => project.id ?? '') ?? [],
    locationIds: searchParameters.locationList?.map(location => location.locationId ?? '') ?? [],
    statusList: searchParameters.taStatusList ?? [],
    createdAfter: searchParameters.dateStart?.toISOString(),
    createdBefore: searchParameters.dateEnd?.toISOString(),
    taTypeList: searchParameters.taTypeList ?? [],
    purposes: searchParameters.purposeList ?? [],
    page: page,
  };
}

@Injectable({
  providedIn: 'root'
})
export class TaSearchDatasource {
  taSubmissionReduxCopy = computed<FullTaDetails | undefined>(() => this.store.taSubmission());

  constructor(
    private store: TaAppStore,
    private loadingService: LoadingService,
    private userApi: UserService,
    private adminApi: AdminService,
    private submissionApi: SubmissionService,
    private reportsApi: ReportsService,
    private errorMessageService: ErrorMessageService,
  ) {}

  // Caching the history ta list. A cache within LIST_CACHE_TTL_MS is reused, so re-entering the tab
  // costs nothing; past that it refetches, which is how another user's action eventually lands.
  loadHistoryTaList(): void {
    if (!isListCacheFresh(this.store.historyTaListCache())) {
      this.store.updateLoadingData(true);
      this.userApi.getUserTAHistory(0).subscribe({
        next: result => {
          if (result.success && result.data?.results) {
            this.store.updateHistoryTaListCache({
              totalCount: result.data.totalCount,
              results: result.data.results.sort((a, b) => {
                const da = a.created?.created ? new Date(a.created.created).getTime() : 0;
                const db = b.created?.created ? new Date(b.created.created).getTime() : 0;

                return db - da; // newest first
              })
            });
          } else {
            this.errorMessageService.showTemporaryError('No user history TA list found.' + result.error, 5000);
            console.error('No user history TA list found.');
          }
          this.store.updateLoadingData(false);
        },
        error: err => {
          console.error(err);
          this.store.updateLoadingData(false);
        }
      });
    }
  }

  // Load next page of history TAs and append to existing results
  loadMoreHistoryTas(): void {
    this.store.updateLoadingData(true);
    this.userApi.getUserTAHistory(this.nextPage(this.store.historyTaListCache())).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          const sorted = result.data.results.sort((a, b) => {
            const da = a.created?.created ? new Date(a.created.created).getTime() : 0;
            const db = b.created?.created ? new Date(b.created.created).getTime() : 0;
            return db - da; // newest first
          });
          this.store.addExtraHistoryTas(sorted);
        } else {
          this.errorMessageService.showTemporaryError('History TA load more failed.' + result.error, 5000);
          console.error('History TA load more failed.');
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  // Caching the approval-history list (the My Approvals History tab on the approvals dashboard).
  // Same TTL rule as the other list caches: re-entering the tab within LIST_CACHE_TTL_MS is free.
  loadApprovalHistoryTaList(): void {
    if (!isListCacheFresh(this.store.approvalHistoryTaListCache())) {
      this.store.updateLoadingData(true);
      this.userApi.getUserApprovalHistory(0).subscribe({
        next: result => {
          if (result.success && result.data?.results) {
            this.store.updateApprovalHistoryTaListCache(result.data);
          } else {
            this.errorMessageService.showTemporaryError('No approval history found.' + result.error, 5000);
            console.error('No approval history found.');
          }
          this.store.updateLoadingData(false);
        },
        error: err => {
          console.error(err);
          this.store.updateLoadingData(false);
        }
      });
    }
  }

  // Load next page of approval history TAs and append to existing results
  loadMoreApprovalHistoryTas(): void {
    this.store.updateLoadingData(true);
    const page = this.nextPage(this.store.approvalHistoryTaListCache());
    this.userApi.getUserApprovalHistory(page).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraApprovalHistoryTas(result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Approval history load more failed.' + result.error, 5000);
          console.error('Approval history load more failed.');
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  loadDirectReportTaList(): void {
    if (!isListCacheFresh(this.store.directReportTaListCache())) {
      this.store.updateLoadingData(true);
      this.userApi.getDirectReportSubmissions(0).subscribe({
        next: result => {
          if (result.success && result.data?.results) {
            this.store.updateDirectReportTaListCache(result.data);
          } else {
            this.errorMessageService.showTemporaryError('No direct report submissions found.' + result.error, 5000);
            console.error('No direct report submissions found.');
          }
          this.store.updateLoadingData(false);
        },
        error: err => {
          console.error(err);
          this.store.updateLoadingData(false);
        }
      });
    }
  }

  loadMoreDirectReportTas(): void {
    this.store.updateLoadingData(true);
    const page = this.nextPage(this.store.directReportTaListCache());
    this.userApi.getDirectReportSubmissions(page).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraDirectReportTas(result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Direct report load more failed.' + result.error, 5000);
          console.error('Direct report load more failed.');
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  // The closed counterpart of loadDirectReportTaList - the My Direct Report TA History tab.
  loadDirectReportHistoryTaList(): void {
    if (!isListCacheFresh(this.store.directReportHistoryTaListCache())) {
      this.store.updateLoadingData(true);
      this.userApi.getDirectReportHistory(0).subscribe({
        next: result => {
          if (result.success && result.data?.results) {
            this.store.updateDirectReportHistoryTaListCache(result.data);
          } else {
            this.errorMessageService.showTemporaryError('No direct report history found.' + result.error, 5000);
            console.error('No direct report history found.');
          }
          this.store.updateLoadingData(false);
        },
        error: err => {
          console.error(err);
          this.store.updateLoadingData(false);
        }
      });
    }
  }

  loadMoreDirectReportHistoryTas(): void {
    this.store.updateLoadingData(true);
    const page = this.nextPage(this.store.directReportHistoryTaListCache());
    this.userApi.getDirectReportHistory(page).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraDirectReportHistoryTas(result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Direct report history load more failed.' + result.error, 5000);
          console.error('Direct report history load more failed.');
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  // Caching the unit manager TA lists (the My Unit TAs and My Unit TAs History tabs).
  loadUnitTaList(mode: UnitTaMode): void {
    const cache = mode === 'active'
      ? this.store.unitActiveTaListCache()
      : this.store.unitClosedTaListCache();
    if (isListCacheFresh(cache)) {
      return;
    }

    this.store.updateLoadingData(true);
    this.fetchUnitPage(mode, 0).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.updateUnitTaListCache(mode, result.data);
        } else {
          this.errorMessageService.showTemporaryError(`No ${mode} unit TAs found.` + result.error, 5000);
          console.error(`No ${mode} unit TAs found.`);
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  loadMoreUnitTas(mode: UnitTaMode): void {
    const cache = mode === 'active'
      ? this.store.unitActiveTaListCache()
      : this.store.unitClosedTaListCache();
    this.store.updateLoadingData(true);
    this.fetchUnitPage(mode, this.nextPage(cache)).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraUnitTas(mode, result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Unit TA load more failed.' + result.error, 5000);
          console.error('Unit TA load more failed.');
        }
        this.store.updateLoadingData(false);
      },
      error: err => {
        console.error(err);
        this.store.updateLoadingData(false);
      },
    });
  }

  // /user/unit-submissions is deliberately unpaged on the server - it is the unit manager's working
  // list - so the active leg ignores the page and its cache reports currentCount === totalCount,
  // which keeps the Load More control hidden.
  private fetchUnitPage(mode: UnitTaMode, page: number): Observable<SubmissionSearchResultRestResponse> {
    return mode === 'active'
      ? this.userApi.getUnitManagerSubmissions()
      : this.userApi.getUnitManagerClosedSubmissions(page);
  }

  // Caching the delegate TA lists (the Active and Closed sections of the My Delegate TAs tab)
  loadDelegateTaList(mode: DelegateTaMode): void {
    const cache = mode === 'active'
      ? this.store.delegateActiveTaListCache()
      : this.store.delegateClosedTaListCache();
    if (isListCacheFresh(cache)) {
      return;
    }

    this.store.updateDelegateLoading(mode, true);
    this.fetchDelegatePage(mode, 0).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.updateDelegateTaListCache(mode, result.data);
        } else {
          this.errorMessageService.showTemporaryError(`No ${mode} delegate TAs found.` + result.error, 5000);
          console.error(`No ${mode} delegate TAs found.`);
        }
        this.store.updateDelegateLoading(mode, false);
      },
      error: err => {
        console.error(err);
        this.store.updateDelegateLoading(mode, false);
      },
    });
  }

  // Load next page of delegate TAs and append to existing results
  loadMoreDelegateTas(mode: DelegateTaMode): void {
    const cache = mode === 'active'
      ? this.store.delegateActiveTaListCache()
      : this.store.delegateClosedTaListCache();
    this.store.updateDelegateLoading(mode, true);
    this.fetchDelegatePage(mode, this.nextPage(cache)).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraDelegateTas(mode, result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Delegate TA load more failed.' + result.error, 5000);
          console.error('Delegate TA load more failed.');
        }
        this.store.updateDelegateLoading(mode, false);
      },
      error: err => {
        console.error(err);
        this.store.updateDelegateLoading(mode, false);
      },
    });
  }

  private nextPage(cache: SearchTaList | undefined): number {
    return cache?.pagesLoaded ?? 0;
  }

  // Both endpoints already return newest-first, so no client-side re-sort is needed here.
  private fetchDelegatePage(mode: DelegateTaMode, page: number): Observable<SubmissionSearchResultRestResponse> {
    return mode === 'active'
      ? this.userApi.getUserDelegateSubmissions(page)
      : this.userApi.getUserClosedDelegateSubmissions(page);
  }

  // Set up basic search call for finance admin dashboard
  basicFinanceSearchSubmissions(searchStr: string, page: number): void {
    this.loadingService.showLoading({message: 'Loading search results...', spinnerSize: 'md'});

    this.adminApi.basicSearchSubmissions(searchStr, page).subscribe({
      next: result => {
        if (result.success && result.data) {
          this.store.updateFinanceSearchResults(result.data);
        } else {
          this.errorMessageService.showTemporaryError('Finance submissions basic search failed.' + result.error, 5000);
          console.error('Finance submissions basic search failed.');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      },
    });
  }

  // Quick Search: leaves the finance search state and loading overlay untouched.
  quickSearchSubmissions(searchStr: string, page: number): Observable<SubmissionSearchResult> {
    return this.adminApi.basicSearchSubmissions(searchStr, page).pipe(
      map(result => {
        if (!result.success || !result.data) {
          throw new Error(result.error ?? 'Quick search failed.');
        }
        return result.data;
      }),
    );
  }

  // Quick Search (advanced): leaves the finance search state and loading overlay untouched.
  quickAdvancedSearchSubmissions(searchParameters: AdvancedSearchParameters, page: number): Observable<SubmissionSearchResult> {
    return this.adminApi.advancedSearchSubmissions(toSubmissionSearchCriteria(searchParameters, page)).pipe(
      map(result => {
        if (!result.success || !result.data) {
          throw new Error(result.error ?? 'Quick search failed.');
        }
        return result.data;
      }),
    );
  }

  // Set up advanced search call for finance admin dashboard
  advancedFinanceSearchSubmissions(searchParameters: AdvancedSearchParameters, page: number): void {
    this.loadingService.showLoading({message: 'Loading search results...', spinnerSize: 'md'});

    const submissionSearchCriteria = toSubmissionSearchCriteria(searchParameters, page);

    this.adminApi.advancedSearchSubmissions(submissionSearchCriteria).subscribe({
      next: result => {
        if (result.success && result.data) {
          console.log(result.data);
          // totalCount?: number;
          // results?: Array<BasicTaDetails> | null;
          this.store.updateFinanceSearchResults(result.data)
        } else {
          this.errorMessageService.showTemporaryError('Finance submissions advance search failed.' + result.error, 5000);
          console.error('Finance submissions advance search failed.');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      },
    });
  }

  // Load next page of advanced finance search and append to existing results
  loadMoreFinanceSearch(searchParameters: AdvancedSearchParameters, page: number): void {
    this.loadingService.showLoading({message: 'Loading search results...', spinnerSize: 'md'});

    const submissionSearchCriteria = toSubmissionSearchCriteria(searchParameters, page);

    this.adminApi.advancedSearchSubmissions(submissionSearchCriteria).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraFinanceSearchResults(result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Finance submissions load more failed.' + result.error, 5000);
          console.error('Finance submissions load more failed.');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      },
    });
  }

  // Load next page of basic finance search and append to existing results
  loadMoreBasicFinanceSearch(searchStr: string, page: number): void {
    this.loadingService.showLoading({message: 'Loading search results...', spinnerSize: 'md'});

    this.adminApi.basicSearchSubmissions(searchStr, page).subscribe({
      next: result => {
        if (result.success && result.data?.results) {
          this.store.addExtraFinanceSearchResults(result.data.results);
        } else {
          this.errorMessageService.showTemporaryError('Finance submissions basic search load more failed.' + result.error, 5000);
          console.error('Finance submissions basic search load more failed.');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      },
    });
  }

  loadProjectOptions(): void {
    if (this.store.availableProjects().length === 0) {
      const task$ = this.submissionApi.getProjectOptions();
      task$.subscribe({
        next: response => {
          if (response.success && response.data) {
            this.store.updateAvailableProjects(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Failed to load project options: ' + response.error, 5000);
          }
        },
        error: error => console.error('Error loading projects:', error),
      });
    }
  }

  loadLocationOptions(): void {
    if (this.store.availableLocations().length === 0 && !this.store.locationsLoading()) {
      // Flagged so screens that render locations (the itinerary table) can keep the loading
      // overlay up until the list lands, instead of painting empty From/To cells first.
      this.store.updateLocationsLoading(true);
      const task$ = this.submissionApi.getLocationOptions();
      task$.subscribe({
        next: response => {
          if (response.success) {
            this.store.updateAvailableLocations(response.data ?? []);
          } else {
            this.errorMessageService.showTemporaryError('Failed to load locations options: ' + response.error, 5000);
          }
          this.store.updateLocationsLoading(false);
        },
        error: error => {
          console.error('Error loading locations:', error);
          this.store.updateLocationsLoading(false);
        },
      });
    }
  }

  loadTravellerOptions(): void {
    if (this.store.availableUsers().length === 0) {
      const task$ = this.submissionApi.getTravellerOptions();
      task$.subscribe({
        next: response => {
          if (response.success && response.data) {
            this.store.updateAvailableUsers(response.data);
          } else {
            this.errorMessageService.showTemporaryError('Failed to load travellers: ' + response.error, 5000);
          }
        },
        error: error => console.error('Error loading travellers:', error),
      });
    }
  }

  loadUserOptionsForSearch(): void {
    this.adminApi.getBasicDetailsUserList().subscribe({
      next: response => {
        if (response.success && response.data) {
          this.store.updateUserSearchOptions(response.data);
        } else {
          this.errorMessageService.showTemporaryError('Failed to load user search options: ' + response.error, 5000);
          console.error('Error loading user search options:', response.error);
        }
      },
      error: error => console.error('Error loading user search options:', error),
    })
  }

  loadApproverOptions(projectId: string): Observable<UserDetailsListRestResponse> {
    return this.submissionApi.getApproverOptions(projectId);
  }

  /** Projects a flight-only TA may use, keyed off the traveller's manager chain. */
  loadFlightOnlyProjectOptions(travellerUserId: string): Observable<ProjectDetailsListRestResponse> {
    return this.submissionApi.getFlightOnlyProjectOptions(travellerUserId);
  }

  /** CEO ids, without touching availablePrimaryApprovers. Errors on an unsuccessful response. */
  loadCeoIds(): Observable<string[]> {
    return this.submissionApi.getCeoOptions().pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.error ?? 'Failed to load CEO options');
        }
        return (response.data ?? []).map(u => u.id ?? '').filter(id => !!id);
      }),
    );
  }

  loadGeneralManagerOptions(): void {
    const task$ = this.submissionApi.getGeneralManagerOptions();
    task$.subscribe({
      next: response => {
        if (response.success) {
          this.store.updateAvailablePrimaryApprovers(response.data ?? []);
        } else {
          this.errorMessageService.showTemporaryError('Failed to load general manager options: ' + response.error, 5000);
        }
      },
      error: error => console.error('Error loading general managers:', error),
    });
  }

  loadCeoOptions(): void {
    const task$ = this.submissionApi.getCeoOptions();
    task$.subscribe({
      next: response => {
        if (response.success) {
          this.store.updateAvailablePrimaryApprovers(response.data ?? []);
        } else {
          this.errorMessageService.showTemporaryError('Failed to load CEO options: ' + response.error, 5000);
        }
      },
      error: error => console.error('Error loading CEO options:', error),
    });
  }

  exportSearchResults(criteria: SubmissionSearchCriteria): void {
    this.loadingService.showLoading({message: 'Exporting...', spinnerSize: 'md'});
    this.reportsApi.getSearchResultsReport(criteria, 'response', false, {
      httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' as any
    }).subscribe({
      next: (response: HttpResponse<any>) => {
        const blob = new Blob([response.body], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        const filenameMatch = disposition?.match(/filename="([^"]+)"|filename=([^;\s]+)/);
        a.download = filenameMatch?.[1] ?? filenameMatch?.[2] ?? 'SearchResults.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error('Export failed', err);
        this.loadingService.hideLoading();
      }
    });
  }
}
